import { useRef, useEffect, useState } from 'react';
import ScrollReveal from '@/components/shared/ScrollReveal';
import {
  ArrowRight,
  Smartphone,
  Signal,
  MessageCircle,
  FileText,
  Tag,
  Clock,
  PiggyBank,
} from 'lucide-react';

const TELECOM_FLYER_URL = `${__BASE_PATH__}assets/flyer-planos-igreen.pdf`;

const benefits = [
  {
    icon: Signal,
    title: 'Dados que acumulam',
    description: 'Os GB não utilizados ficam para o próximo mês.',
  },
  {
    icon: MessageCircle,
    title: 'Atendimento 24h',
    description: 'Plataforma digital completa, fácil e ágil.',
  },
  {
    icon: FileText,
    title: 'Sem fidelidade',
    description: 'Altere seu plano quando quiser, sem multa.',
  },
  {
    icon: Tag,
    title: 'iGreen Club',
    description: 'Descontos em mais de 600 mil produtos e serviços.',
  },
  {
    icon: Clock,
    title: 'Consumo em tempo real',
    description: 'Acompanhe tudo com transparência e sem burocracia.',
  },
  {
    icon: PiggyBank,
    title: 'Cashback Telecom',
    description: 'Indique novos clientes, aumente seu desconto e até mesmo zere o valor do plano.',
  },
];

function BenefitCard({ item, index }: { item: typeof benefits[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group flex h-full items-start gap-3 p-3 md:p-4 rounded-xl bg-white border border-background-200/70 transition-all duration-500 hover:-translate-y-0.5 hover:shadow-md hover:border-secondary-500/30 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="w-12 h-12 rounded-xl bg-secondary-100/80 ring-1 ring-secondary-200/50 flex items-center justify-center text-secondary-600 shrink-0 transition-transform duration-300 group-hover:scale-110">
        <item.icon className="w-6 h-6" />
      </div>
      <div className="min-w-0">
        <h4 className="text-sm md:text-base font-bold text-foreground-950 mb-0.5">{item.title}</h4>
        <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.description}</p>
      </div>
    </div>
  );
}

export default function TelecomSection() {
  return (
    <section id="telecom" className="immersive-panel bg-background-100">
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-radial-green-sm rounded-full opacity-30 pointer-events-none" />

      <div className="split-viewport">
        <div className="contents">
          {/* Content */}
          <ScrollReveal className="split-viewport__content order-2 lg:order-1 text-center lg:text-left">
            <span className="inline-flex w-auto self-center lg:self-start items-center gap-2 px-4 py-1.5 rounded-full bg-secondary-100 border border-secondary-200 text-secondary-700 text-sm font-semibold mb-5">
              <Smartphone className="w-4 h-4" />
              iGreen Telecom
            </span>

            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tighter leading-[1.1]">
              Conecte-se com a <br className="hidden md:inline" /><span className="text-gradient">iGreen Telecom</span>
            </h2>

            <p className="mt-4 md:mt-5 text-base md:text-lg text-foreground-600 leading-relaxed">
              Planos flexíveis, benefícios exclusivos e controle total na palma da sua mão.
            </p>

            {/* Benefit grid */}
            <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {benefits.map((item, i) => (
                <BenefitCard key={item.title} item={item} index={i} />
              ))}
            </div>

            {/* CTA */}
            <div className="mt-6 md:mt-8">
              <a
                href={TELECOM_FLYER_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-primary w-full sm:w-auto text-sm md:text-base px-5 md:px-8 py-3.5 md:py-4 group"
              >
                Conhecer os planos
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </ScrollReveal>

          {/* Image */}
          <ScrollReveal className="split-viewport__media order-1 lg:order-2" delay={120}>
            <div className="relative h-full">
              <div className="relative h-full overflow-hidden bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20cheerful%20Brazilian%20family%20of%20four%20relaxing%20together%20on%20a%20comfortable%20modern%20sofa%20in%20a%20bright%20living%20room%2C%20a%20father%20showing%20a%20smartphone%20screen%20to%20his%20wife%20and%20two%20children%20who%20are%20smiling%20and%20engaged%2C%20warm%20natural%20light%2C%20clean%20contemporary%20home%20with%20neutral%20tones%20and%20green%20plants%2C%20lifestyle%20editorial%20photography%2C%20shallow%20depth%20of%20field%2C%20high%20detail&width=900&height=1200&seq=telecom-lifestyle-01&orientation=portrait"
                  alt="Família usando smartphone com os serviços de conectividade da iGreen Telecom"
                  title="Conecte-se com a iGreen Telecom"
                  className="h-full w-full object-cover object-center"
                  width="900"
                  height="1200"
                />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
